import { useRef, useState } from 'react'
import { BarChart3, FileCheck2, Paperclip, Trash2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import {
  RichTextEditor,
  type RichTextEditorHandle,
} from '@/components/announcements/rich-text-editor'
import {
  useAddAnnouncementLink,
  useAnnouncement,
  useCreateAnnouncement,
  useDeleteAnnouncementAttachment,
  useRemoveAnnouncementLink,
  useUpdateAnnouncement,
  useUploadAnnouncementAttachment,
  useUploadAnnouncementInlineImage,
} from '@/hooks/use-announcements'
import { usePolls } from '@/hooks/use-polls'
import { useResolutions } from '@/hooks/use-resolutions'
import { getApiErrorMessage } from '@/lib/api'
import { publicDocumentUrl } from '@/lib/documents'
import { stripHtmlToText } from '@riverpark/utils/html'
import type {
  Announcement,
  AnnouncementCategory,
  AnnouncementLinkEntityType,
} from '@riverpark/types'

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const CATEGORIES: { value: AnnouncementCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'financial', label: 'Financial' },
  { value: 'governance', label: 'Governance' },
]

export function AnnouncementModal({
  announcement,
  prefill,
  onClose,
}: {
  /** Omit to create a new announcement instead of editing one. */
  announcement?: Announcement
  /** Only used in create mode — e.g. "Publish as announcement" from a poll/resolution page.
   * `link` auto-links the new announcement to that poll/resolution once it's created. */
  prefill?: {
    title?: string
    body?: string
    category?: AnnouncementCategory
    link?: { entityType: AnnouncementLinkEntityType; entityId: string }
  }
  onClose: () => void
}) {
  const create = useCreateAnnouncement()
  const update = useUpdateAnnouncement()
  const uploadAttachment = useUploadAnnouncementAttachment()
  const deleteAttachment = useDeleteAnnouncementAttachment()
  const uploadInlineImage = useUploadAnnouncementInlineImage()
  const addLink = useAddAnnouncementLink()
  const removeLink = useRemoveAnnouncementLink()
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const isEdit = !!announcement
  // `announcement` is a snapshot from the list the modal was opened from — it never
  // updates on its own, so attachment add/remove (which happens inline, outside the
  // main form submit) would otherwise never show up. Subscribe to the live query
  // instead, seeded with that snapshot so it renders immediately while refetching.
  const { data: liveAnnouncement } = useAnnouncement(announcement?.id)
  const attachments = liveAnnouncement?.data.attachments ?? announcement?.attachments ?? []
  const links = liveAnnouncement?.data.links ?? announcement?.links ?? []

  // For the "link a poll or resolution" picker — any poll/resolution can be linked, not just
  // closed ones (an announcement might say "vote now" while a poll is still open).
  const { data: pollsData } = usePolls({ limit: 100 })
  const { data: resolutionsData } = useResolutions({ limit: 100 })

  const [title, setTitle] = useState(announcement?.title ?? prefill?.title ?? '')
  const [body, setBody] = useState(announcement?.body ?? prefill?.body ?? '')
  const [category, setCategory] = useState<AnnouncementCategory>(
    announcement?.category ?? prefill?.category ?? 'general',
  )
  const [pinned, setPinned] = useState(announcement?.pinned ?? false)
  const [isPublic, setIsPublic] = useState(announcement?.public ?? false)
  const [isDraft, setIsDraft] = useState(announcement?.status === 'draft')
  const [sendSms, setSendSms] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  // Create mode has no announcement id yet to attach links to — stage them locally and add them
  // right after the announcement itself is created, same pattern as pendingFile/pending images.
  const [pendingLinks, setPendingLinks] = useState<
    { entityType: AnnouncementLinkEntityType; entityId: string; title: string }[]
  >([])
  const [linkSelection, setLinkSelection] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saveStep, setSaveStep] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const pending =
    create.isPending ||
    update.isPending ||
    uploadAttachment.isPending ||
    uploadInlineImage.isPending ||
    addLink.isPending
  // Broadcasting (SMS/email) only makes sense the moment an announcement actually goes live —
  // either a brand-new one posted straight away, or an existing draft being published now. An
  // already-published announcement being re-saved never re-broadcasts.
  const willPublishNow = !isDraft && (!isEdit || announcement?.status === 'draft')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (stripHtmlToText(body).length < 10) {
      setError('Body must be at least 10 characters')
      return
    }
    try {
      if (isEdit) {
        setSaveStep(isDraft ? 'Saving draft…' : willPublishNow ? 'Publishing…' : 'Saving changes…')
        await update.mutateAsync({
          id: announcement.id,
          input: {
            title,
            body,
            category,
            pinned,
            public: isPublic,
            status: isDraft ? 'draft' : 'published',
            ...(willPublishNow && { sendSms, sendEmail }),
          },
        })
      } else {
        setSaveStep(isDraft ? 'Saving draft…' : 'Posting announcement…')
        const result = await create.mutateAsync({
          title,
          body,
          category,
          pinned,
          public: isPublic,
          status: isDraft ? 'draft' : 'published',
          sendSms,
          sendEmail,
        })
        const announcementId = result.data.id

        // Images inserted while composing had no announcement to attach to yet, so they were
        // staged as local blob: previews — upload each now that a real id exists, then patch the
        // body to swap those local previews for the real hosted URLs.
        const pendingImages = richTextEditorRef.current?.getPendingImages() ?? new Map()
        if (pendingImages.size > 0) {
          let finalBody = body
          let uploaded = 0
          for (const [localUrl, file] of pendingImages) {
            uploaded += 1
            setSaveStep(`Uploading image ${uploaded} of ${pendingImages.size}…`)
            const uploadResult = await uploadInlineImage.mutateAsync({ announcementId, file })
            finalBody = finalBody.split(localUrl).join(publicDocumentUrl(uploadResult.data.id))
            URL.revokeObjectURL(localUrl)
          }
          setSaveStep('Finalizing…')
          await update.mutateAsync({ id: announcementId, input: { body: finalBody } })
        }

        if (pendingFile) {
          setSaveStep('Uploading attachment…')
          await uploadAttachment.mutateAsync({ announcementId, file: pendingFile })
        }

        const allLinks = [
          ...(prefill?.link ? [prefill.link] : []),
          ...pendingLinks.map((l) => ({ entityType: l.entityType, entityId: l.entityId })),
        ]
        for (const link of allLinks) {
          setSaveStep('Linking…')
          await addLink.mutateAsync({
            announcementId,
            entityType: link.entityType,
            entityId: link.entityId,
          })
        }
      }
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save announcement'))
    } finally {
      setSaveStep(null)
    }
  }

  async function handleAddAttachment(file: File) {
    if (!announcement) return
    setError(null)
    try {
      await uploadAttachment.mutateAsync({ announcementId: announcement.id, file })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload attachment'))
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDeleteAttachment(documentId: string) {
    if (!announcement) return
    try {
      await deleteAttachment.mutateAsync({ announcementId: announcement.id, documentId })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to remove attachment'))
    }
  }

  async function handleAddLink() {
    if (!linkSelection) return
    const [entityType, entityId] = linkSelection.split(':') as [AnnouncementLinkEntityType, string]
    const title =
      entityType === 'poll'
        ? pollsData?.data.find((p) => p.id === entityId)?.title
        : resolutionsData?.data.find((r) => r.id === entityId)?.title
    if (!title) return
    setLinkSelection('')

    if (!announcement) {
      setPendingLinks((prev) => [...prev, { entityType, entityId, title }])
      return
    }
    try {
      await addLink.mutateAsync({ announcementId: announcement.id, entityType, entityId })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to add link'))
    }
  }

  async function handleRemoveLink(linkId: string) {
    if (!announcement) {
      setPendingLinks((prev) => prev.filter((_, i) => String(i) !== linkId))
      return
    }
    try {
      await removeLink.mutateAsync({ announcementId: announcement.id, linkId })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to remove link'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="announcement-modal-title" panelClassName="max-w-2xl">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 id="announcement-modal-title" className="font-serif text-xl text-ink">
              {isEdit ? 'Edit announcement' : 'Post announcement'}
            </h2>
            {announcement?.status === 'draft' && <Badge variant="draft">Draft</Badge>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            minLength={3}
            maxLength={200}
          />
          <div className="space-y-1.5">
            <label htmlFor="announcement-body" className="font-sans text-sm font-medium text-ink">
              Body
            </label>
            <RichTextEditor
              ref={richTextEditorRef}
              id="announcement-body"
              value={body}
              onChange={setBody}
              announcementId={announcement?.id}
            />
          </div>
          <div className="space-y-1.5">
            <label className="font-sans text-sm font-medium text-ink">Category</label>
            <select
              className={SELECT_CLS}
              value={category}
              onChange={(e) => setCategory(e.target.value as AnnouncementCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="font-sans text-sm font-medium text-ink">
              Attachment{isEdit ? 's' : ''} (optional)
            </label>
            {isEdit && attachments.length > 0 && (
              <ul className="space-y-1.5">
                {attachments.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex items-center justify-between gap-2 rounded border border-border-2 bg-card px-3 py-2"
                  >
                    <span className="flex min-w-0 items-center gap-2 font-sans text-sm text-ink">
                      <Paperclip size={14} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">{doc.title}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {formatFileSize(doc.sizeBytes)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteAttachment(doc.id)}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label={`Remove ${doc.title}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {isEdit ? (
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleAddAttachment(file)
                }}
                className="block w-full font-sans text-sm text-ink"
              />
            ) : (
              <input
                type="file"
                onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
                className="block w-full font-sans text-sm text-ink"
              />
            )}
          </div>

          <div className="space-y-2">
            <label className="font-sans text-sm font-medium text-ink">
              Linked polls/resolutions (optional)
            </label>
            {(isEdit ? links : pendingLinks).length > 0 && (
              <ul className="space-y-1.5">
                {(isEdit
                  ? links.map((l) => ({ id: l.id, title: l.title, entityType: l.entityType }))
                  : pendingLinks.map((l, i) => ({
                      id: String(i),
                      title: l.title,
                      entityType: l.entityType,
                    }))
                ).map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-2 rounded border border-border-2 bg-card px-3 py-2"
                  >
                    <span className="flex min-w-0 items-center gap-2 font-sans text-sm text-ink">
                      {l.entityType === 'poll' ? (
                        <BarChart3 size={14} className="shrink-0 text-muted-foreground" />
                      ) : (
                        <FileCheck2 size={14} className="shrink-0 text-muted-foreground" />
                      )}
                      <span className="truncate">{l.title}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(l.id)}
                      className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                      aria-label={`Remove link to ${l.title}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2">
              <select
                className={SELECT_CLS}
                value={linkSelection}
                onChange={(e) => setLinkSelection(e.target.value)}
              >
                <option value="">Select a poll or resolution…</option>
                {pollsData && pollsData.data.length > 0 && (
                  <optgroup label="Polls">
                    {pollsData.data.map((poll) => (
                      <option key={poll.id} value={`poll:${poll.id}`}>
                        {poll.title}
                      </option>
                    ))}
                  </optgroup>
                )}
                {resolutionsData && resolutionsData.data.length > 0 && (
                  <optgroup label="Resolutions">
                    {resolutionsData.data.map((resolution) => (
                      <option key={resolution.id} value={`resolution:${resolution.id}`}>
                        {resolution.title}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddLink}
                disabled={!linkSelection}
              >
                Add
              </Button>
            </div>
          </div>

          <label className="flex items-center gap-2 font-sans text-sm text-ink">
            <input
              type="checkbox"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
              className="size-4 rounded border-input"
            />
            Save as draft (don't publish yet)
          </label>
          <label className="flex items-center gap-2 font-sans text-sm text-ink">
            <input
              type="checkbox"
              checked={pinned}
              onChange={(e) => setPinned(e.target.checked)}
              className="size-4 rounded border-input"
            />
            Pin to top of the feed
          </label>
          <label className="flex items-center gap-2 font-sans text-sm text-ink">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="size-4 rounded border-input"
            />
            Publish to the public website
          </label>

          {willPublishNow && (
            <>
              <label className="flex items-center gap-2 font-sans text-sm text-ink">
                <input
                  type="checkbox"
                  checked={sendSms}
                  onChange={(e) => setSendSms(e.target.checked)}
                  className="size-4 rounded border-input"
                />
                Also send as SMS to every active member
              </label>
              <label className="flex items-center gap-2 font-sans text-sm text-ink">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="size-4 rounded border-input"
                />
                Also email every active member
              </label>
            </>
          )}
          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {saveStep ??
              (isDraft
                ? 'Save draft'
                : !isEdit
                  ? 'Post announcement'
                  : announcement?.status === 'draft'
                    ? 'Publish'
                    : 'Save changes')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
